import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { createRoot, type Root } from "react-dom/client";
import { EditorContent, useEditor, type AnyExtension, type Editor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { generateHTML } from "@tiptap/html";
import type { TiptapDoc } from "./rich-text-doc";

export type Member = { displayName: string; id: string };

const EMPTY_DOC: TiptapDoc = { content: [{ type: "paragraph" }], type: "doc" };

// Tiptap's default Mention command stores whatever's passed to it verbatim
// as the inserted node's attrs -- the schema only declares {id, label}, so
// passing a raw Member (which has `displayName`, not `label`) silently
// produces a mention with label: null instead of failing loudly.
export const memberToMentionAttrs = (member: Member): { id: string; label: string } =>
  ({ id: member.id, label: member.displayName });

// A plain, dependency-free (no tippy.js) floating mention-suggestion panel,
// imperatively mounted/updated/unmounted per Tiptap's Suggestion render()
// lifecycle. Reuses the existing .mention-suggestions CSS as-is -- that
// class already implements exactly this UI pattern for the old
// MentionTextarea's custom dropdown.
const mentionSuggestionRender = () => {
  let container: HTMLDivElement | null = null;
  let root: Root | null = null;
  let selectedIndex = 0;
  let currentItems: Member[] = [];
  let currentCommand: ((attrs: { id: string; label: string }) => void) | null = null;

  const destroy = () => {
    root?.unmount();
    container?.remove();
    root = null;
    container = null;
  };

  const selectMember = (member: Member) => currentCommand?.(memberToMentionAttrs(member));

  const renderList = () => {
    if (!root) return;
    root.render(
      <div aria-label="People suggestions" className="mention-suggestions" role="listbox">
        {currentItems.length
          ? currentItems.map((member, index) => (
              <button
                aria-selected={index === selectedIndex}
                key={member.id}
                onMouseDown={(event) => { event.preventDefault(); selectMember(member); }}
                role="option"
                style={index === selectedIndex ? { background: "var(--jira-blue-soft)" } : undefined}
                type="button"
              >
                <strong>{member.displayName}</strong>
              </button>
            ))
          : <p className="rich-text-preview-empty">No matches</p>}
      </div>,
    );
  };

  const positionAt = (clientRect: (() => DOMRect | null) | null | undefined) => {
    if (!container) return;
    const rect = clientRect?.();
    if (!rect) return;
    container.style.left = `${rect.left}px`;
    container.style.top = `${rect.bottom + 4}px`;
  };

  return {
    onExit: destroy,
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === "Escape") { destroy(); return true; }
      if (!currentItems.length) return false;
      if (event.key === "ArrowDown") { selectedIndex = (selectedIndex + 1) % currentItems.length; renderList(); return true; }
      if (event.key === "ArrowUp") { selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length; renderList(); return true; }
      if (event.key === "Enter" || event.key === "Tab") { selectMember(currentItems[selectedIndex]); return true; }
      return false;
    },
    onStart: (props: { clientRect?: (() => DOMRect | null) | null; command: (attrs: { id: string; label: string }) => void; items: Member[] }) => {
      currentItems = props.items;
      currentCommand = props.command;
      selectedIndex = 0;
      container = document.createElement("div");
      container.style.position = "fixed";
      // Above .dialog-backdrop (1000) and .toast-region (1200) -- this panel
      // is appended straight to document.body, so it needs to out-rank every
      // other fixed-position overlay's stacking context, not just sit above
      // its own DOM siblings.
      container.style.zIndex = "1300";
      document.body.appendChild(container);
      root = createRoot(container);
      positionAt(props.clientRect);
      renderList();
    },
    onUpdate: (props: { clientRect?: (() => DOMRect | null) | null; command: (attrs: { id: string; label: string }) => void; items: Member[] }) => {
      currentItems = props.items;
      currentCommand = props.command;
      selectedIndex = 0;
      positionAt(props.clientRect);
      renderList();
    },
  };
};

// Only in the live editor -- read-only rendering (RichTextView/generateHTML)
// must never offer a delete affordance, so the plain Image extension (no
// node view) is used there instead. A vanilla-DOM NodeView, not a React one
// (ReactNodeViewRenderer) -- a hover-delete overlay on an <img> doesn't need
// React's own reconciliation, and this keeps the dependency footprint the
// same as everything else in this file.
const EditableImage = Image.extend({
  addNodeView() {
    return ({ editor, getPos, node }) => {
      const wrapper = document.createElement("span");
      wrapper.className = "rich-text-image-wrapper";
      const img = document.createElement("img");
      img.className = "rich-text-image";
      img.src = String(node.attrs.src ?? "");
      if (node.attrs.alt) img.alt = String(node.attrs.alt);
      const deleteButton = document.createElement("button");
      deleteButton.type = "button";
      deleteButton.className = "rich-text-image-delete";
      deleteButton.setAttribute("aria-label", "Remove image");
      deleteButton.textContent = "×";
      deleteButton.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const pos = typeof getPos === "function" ? getPos() : undefined;
        if (typeof pos !== "number") return;
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      });
      wrapper.append(img, deleteButton);
      return { dom: wrapper };
    };
  },
});

const buildExtensions = ({
  characterLimit, editable, members = [], placeholder,
}: { characterLimit?: number; editable?: boolean; members?: Member[]; placeholder?: string }): AnyExtension[] => {
  const extensions: AnyExtension[] = [
    // codeBlock's class matches .rich-text-body's existing "pre.rich-text-code"
    // CSS selector, reused as-is from the old hand-rolled renderer.
    StarterKit.configure({ codeBlock: { HTMLAttributes: { class: "rich-text-code" } }, link: { openOnClick: false } }),
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    (editable ? EditableImage : Image).configure({ HTMLAttributes: { class: "rich-text-image" } }),
    // renderHTML/renderText drive both the live editor and read-only static
    // rendering (RichTextView below) -- one code path, no drift between
    // edit-mode and display-mode mention appearance, unlike the old system
    // where the editing textarea showed raw "@FullName" text and only the
    // separate RichText renderer highlighted it.
    Mention.configure({
      HTMLAttributes: { class: "comment-mention" },
      renderHTML: ({ node }) => ["span", { class: "comment-mention" }, `@${node.attrs.label}`],
      renderText: ({ node }) => `@${node.attrs.label}`, // must match rich-text-doc.ts's docToPlainText mention convention exactly
      suggestion: {
        char: "@",
        items: ({ query }: { query: string }) =>
          members.filter((member) => member.displayName.toLowerCase().includes(query.toLowerCase())).slice(0, 10),
        render: mentionSuggestionRender,
      },
    }),
  ];
  if (placeholder) extensions.push(Placeholder.configure({ placeholder }));
  if (characterLimit) extensions.push(CharacterCount.configure({ limit: characterLimit }));
  return extensions;
};

// A single shared, member-agnostic extension set for read-only rendering
// (RichTextView) -- mentions render from their own stored {id,label} attrs,
// so no live member list or suggestion behavior is needed here.
const READ_ONLY_EXTENSIONS = buildExtensions({});

/** Static HTML for read-only display -- deliberately not a mounted live Editor instance (see the Tiptap plan's rendering-strategy section: a comment thread or board can show many items at once, and N live ProseMirror EditorViews for content that never needs interaction is real, avoidable overhead). */
export const renderRichTextHTML = (doc: TiptapDoc | null): string => {
  if (!doc?.content?.length) return "";
  try {
    return generateHTML(doc, READ_ONLY_EXTENSIONS);
  } catch {
    return "";
  }
};

export function RichTextView({ className, doc }: { className?: string; doc: TiptapDoc | null }) {
  const html = useMemo(() => renderRichTextHTML(doc), [doc]);
  if (!html) return null;
  // Safe here specifically because this HTML comes from our own
  // schema-constrained generateHTML output, not arbitrary user HTML --
  // same trust model the old hand-rolled RichText parser had, just
  // swapping who builds the DOM.
  return <div className={className ? `rich-text-body ${className}` : "rich-text-body"} dangerouslySetInnerHTML={{ __html: html }} />;
}

function ToolbarButton({
  active, disabled, icon, label, onClick,
}: { active: boolean; disabled?: boolean; icon: string; label: string; onClick: () => void }) {
  return (
    <button aria-label={label} aria-pressed={active} disabled={disabled} onClick={onClick} tabIndex={-1} title={label} type="button">
      <i aria-hidden="true" className={`bi ${icon}`} />
    </button>
  );
}

// Matches the server's limits (tasks-api.ts) -- checked client-side purely
// to fail fast with a clear message instead of waiting on a round trip to
// the presign endpoint for a file that was always going to be rejected.
const ALLOWED_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export function RichTextToolbar({ editor, taskId }: { editor: Editor; taskId?: string }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setLink = () => {
    const existing = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", existing ?? "https://");
    if (url === null) return;
    if (!url) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const onImageSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // lets the same file be re-selected later
    if (!file || !taskId) return;
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) { setUploadError("Choose a PNG, JPEG, GIF, or WEBP image."); return; }
    if (file.size > MAX_IMAGE_BYTES) { setUploadError("That image is too large (max 10MB)."); return; }
    setUploading(true); setUploadError("");
    try {
      const presignResponse = await fetch(`/api/tasks/${taskId}/attachments/presign`, {
        body: JSON.stringify({ contentType: file.type, fileName: file.name, sizeBytes: file.size }),
        headers: { "content-type": "application/json" },
        method: "POST",
      });
      const presignPayload = await presignResponse.json() as { key?: string; message?: string; uploadUrl?: string };
      if (!presignResponse.ok || !presignPayload.uploadUrl || !presignPayload.key) {
        throw new Error(presignPayload.message || "Could not prepare the upload.");
      }
      const uploadResponse = await fetch(presignPayload.uploadUrl, { body: file, method: "PUT" });
      if (!uploadResponse.ok) throw new Error("Could not upload the image.");
      // Always the stable inline-download redirect endpoint, never the raw
      // (expiring) presigned URL -- see rich-text-doc.ts's isValidImageSrc.
      const src = `/api/attachments/inline-download/${encodeURIComponent(presignPayload.key)}`;
      editor.chain().focus().setImage({ alt: file.name, src }).run();
    } catch (reason) {
      setUploadError(reason instanceof Error ? reason.message : "Could not upload the image.");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div aria-label="Formatting" className="rich-text-toolbar" role="toolbar">
      <ToolbarButton active={editor.isActive("bold")} icon="bi-type-bold" label="Bold" onClick={() => editor.chain().focus().toggleBold().run()} />
      <ToolbarButton active={editor.isActive("italic")} icon="bi-type-italic" label="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} />
      <ToolbarButton active={editor.isActive("strike")} icon="bi-type-strikethrough" label="Strikethrough" onClick={() => editor.chain().focus().toggleStrike().run()} />
      <ToolbarButton active={editor.isActive("code")} icon="bi-code" label="Code" onClick={() => editor.chain().focus().toggleCode().run()} />
      <ToolbarButton active={editor.isActive("link")} icon="bi-link-45deg" label="Link" onClick={setLink} />
      <ToolbarButton active={editor.isActive("bulletList")} icon="bi-list-ul" label="Bulleted list" onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <ToolbarButton active={editor.isActive("orderedList")} icon="bi-list-ol" label="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <ToolbarButton active={editor.isActive("blockquote")} icon="bi-quote" label="Blockquote" onClick={() => editor.chain().focus().toggleBlockquote().run()} />
      <ToolbarButton active={editor.isActive("heading", { level: 2 })} icon="bi-type-h2" label="Heading" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
      {taskId && (
        <>
          <ToolbarButton active={false} disabled={uploading} icon={uploading ? "bi-hourglass-split" : "bi-image"} label="Insert image" onClick={() => fileInputRef.current?.click()} />
          <input accept="image/png,image/jpeg,image/gif,image/webp" aria-hidden="true" hidden onChange={(event) => void onImageSelected(event)} ref={fileInputRef} tabIndex={-1} type="file" />
        </>
      )}
      {uploadError && <span className="rich-text-toolbar-error" role="alert">{uploadError}</span>}
    </div>
  );
}

export type RichTextEditorProps = {
  ariaLabel?: string;
  autofocus?: boolean;
  characterLimit: number;
  disabled?: boolean;
  members?: Member[];
  onChange: (value: TiptapDoc, plainTextLength: number) => void;
  onSubmit?: () => void;
  placeholder?: string;
  submitOnEnter?: boolean;
  // Scopes the toolbar's "Insert image" upload to a task (presigned against
  // that task's S3 key prefix -- see presignAttachmentUpload in
  // tasks-api.ts). Omitted for surfaces with no task to scope an upload to
  // (retro cards), which simply don't get the image button.
  taskId?: string;
  value: TiptapDoc | null;
};

/**
 * The one editor component for all three rich-text surfaces (task
 * descriptions, comments, retro cards) -- see the Tiptap plan's editor
 * section for why nothing left needs a genuinely different extension set
 * per surface. `submitOnEnter` is the one real remaining per-surface
 * behavioral fork (retro cards' fast "drop a note, keep typing" workflow).
 */
export function RichTextEditor({
  ariaLabel, autofocus, characterLimit, disabled, members = [], onChange, onSubmit,
  placeholder, submitOnEnter, taskId, value,
}: RichTextEditorProps) {
  // Extensions are built once per mount -- a live member-list change after
  // mount (someone joins the team mid-edit) is a rare, acceptable-to-miss
  // edge case for a first version; rebuilding the extension array on every
  // members-prop change would tear down and remount the whole editor.
  // The extension array is built once per mount (see below), so the Enter
  // shortcut must reach `onSubmit` through a ref -- capturing the prop directly
  // would freeze it to the first render's closure, which (because the retro
  // composer passes a fresh `() => addCard(...)` each render that in turn
  // closes over that render's draft state) meant Enter fired a submit that
  // always saw an empty draft and silently did nothing.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

  const extensions = useMemo(() => {
    const list = buildExtensions({ characterLimit, editable: true, members, placeholder });
    if (submitOnEnter) {
      list.push(Extension.create({
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              // Inside a structured block, Enter keeps its native meaning --
              // split the list item (continuing the bullet/number list),
              // add a line to a code block or quote -- rather than submitting
              // the note. Only a plain paragraph's Enter is the "add" action.
              const { editor } = this;
              if (
                editor.isActive("listItem")
                || editor.isActive("codeBlock")
                || editor.isActive("blockquote")
              ) {
                return false;
              }
              onSubmitRef.current?.();
              return true;
            },
            "Shift-Enter": () => this.editor.commands.setHardBreak(),
          };
        },
        name: "submitOnEnter",
      }));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editor = useEditor({
    autofocus: autofocus ? "end" : false,
    content: value ?? EMPTY_DOC,
    editable: !disabled,
    // aria-label goes here, not as a prop on <EditorContent> below --
    // EditorContent only renders a wrapper div and appends ProseMirror's
    // actual contenteditable element as a child of it (see @tiptap/react's
    // PureEditorContent.init), so an aria-label prop on EditorContent lands
    // on the wrong element for anything (a11y tooling, Playwright's
    // getByLabel/.fill()) that expects the accessible name on the
    // contenteditable node itself.
    editorProps: { attributes: ariaLabel ? { "aria-label": ariaLabel } : {} },
    extensions,
    onUpdate: ({ editor: instance }) => {
      onChange(instance.getJSON() as TiptapDoc, instance.storage.characterCount.characters());
    },
  });

  // Keeps the editor in sync if `value` changes out from under it (e.g. an
  // async task load finishing after the editor already mounted with a
  // null/placeholder value) -- Tiptap otherwise only reads `content` on
  // initial mount, like an uncontrolled input.
  useEffect(() => {
    if (!editor) return;
    const next = value ?? EMPTY_DOC;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(next)) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => { editor?.setEditable(!disabled); }, [disabled, editor]);

  if (!editor) return null;
  const characters = editor.storage.characterCount.characters();
  const overLimit = characters > characterLimit;
  const nearLimit = !overLimit && characters > characterLimit * 0.9;

  return (
    <div className="tiptap-editor-wrapper">
      <RichTextToolbar editor={editor} taskId={taskId} />
      <EditorContent className="tiptap-editor" editor={editor} />
      <div className="tiptap-editor-footer">
        <span className={`char-counter${overLimit ? " char-counter-over" : nearLimit ? " char-counter-warning" : ""}`}>
          {characters}/{characterLimit}
        </span>
      </div>
    </div>
  );
}
